'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ArrowRight, Calendar, ChevronDown, ChevronUp, RefreshCw, Radio, Edit2, CheckCircle2, Loader2 } from 'lucide-react';
import Badge, { dutyStatusVariant } from '@/components/ui/Badge';
import { dutyService, DutyAssignment } from '@/lib/supabase/services/dutyService';
import { useRealtimeDutyToast } from '@/lib/hooks/useRealtimeDutyToast';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';

function getDateLabel(date: Date): string {
  return date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' });
}

function getDateISO(date: Date): string {
  return date.toISOString().split('T')[0];
}

interface DayDutiesProps {
  label: string;
  dateISO: string;
  duties: DutyAssignment[];
  loading: boolean;
  defaultExpanded?: boolean;
  completingId: string | null;
  onMarkComplete: (duty: DutyAssignment) => void;
}

function DayDuties({ label, dateISO, duties, loading, defaultExpanded = true, completingId, onMarkComplete }: DayDutiesProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const router = useRouter();

  const shiftColor: Record<string, string> = {
    Morning: 'bg-warning/12 text-warning',
    Afternoon: 'bg-primary/12 text-primary',
    Night: 'bg-purple-500/12 text-purple-500',
  };

  return (
    <div className="border-b border-[var(--glass-border)] last:border-0">
      {/* Day header */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-black/3 dark:hover:bg-white/3 transition-colors duration-150"
      >
        <div className="flex items-center gap-2.5">
          <Calendar size={14} className="text-primary shrink-0" />
          <span className="text-sm font-700 text-foreground">{label}</span>
          <span className="text-[11px] font-600 px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {loading ? '…' : `${duties.length} ${duties.length === 1 ? 'duty' : 'duties'}`}
          </span>
        </div>
        {expanded ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
      </button>

      {/* Duty rows */}
      {expanded && (
        <div className="overflow-x-auto scrollbar-thin">
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading duties…</div>
          ) : duties.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No duties scheduled for this day</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black/3 dark:bg-white/3">
                  {['Route', 'Bus', 'Driver', 'Conductor', 'Shift', 'Departure', 'Status', 'Actions'].map((h) => (
                    <th key={h} className={`text-left px-4 py-2.5 text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap ${h === 'Actions' ? 'text-right' : ''}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {duties.map((duty) => {
                  const isMissing = duty.driver1Name === '—' || duty.conductorName === '—';
                  const isCompleting = completingId === duty.id;
                  return (
                    <tr
                      key={duty.id}
                      className={`border-t border-[var(--glass-border)] transition-colors duration-150 hover:bg-black/3 dark:hover:bg-white/3 ${isMissing ? 'bg-negative/3' : ''}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="font-700 text-foreground">{duty.route}</p>
                        <p className="text-[11px] text-muted-foreground truncate max-w-[140px]">{duty.routeName}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{duty.busNumber}</td>
                      <td className={`px-4 py-3 whitespace-nowrap font-500 ${duty.driver1Name === '—' ? 'text-negative' : 'text-foreground'}`}>
                        {duty.driver1Name}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap font-500 ${duty.conductorName === '—' ? 'text-negative' : 'text-foreground'}`}>
                        {duty.conductorName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[11px] font-600 px-2 py-0.5 rounded-md ${shiftColor[duty.shift] ?? 'bg-muted/20 text-muted-foreground'}`}>
                          {duty.shift}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-sm font-600 text-foreground tabular-nums">
                        {duty.departureTime}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={dutyStatusVariant(duty.status)}>{duty.status}</Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            title="Mark as Completed"
                            disabled={isCompleting}
                            onClick={() => onMarkComplete(duty)}
                            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-600 bg-positive/10 text-positive hover:bg-positive/20 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {isCompleting ? (
                              <Loader2 size={11} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={11} />
                            )}
                            {isCompleting ? 'Completing…' : 'Complete'}
                          </button>
                          <button
                            title="Edit"
                            onClick={() => router.push('/duty-assignment')}
                            className="p-1.5 rounded-lg hover:bg-black/8 dark:hover:bg-white/8 text-muted-foreground hover:text-foreground transition-all duration-150"
                          >
                            <Edit2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function UpcomingDutiesDashboard() {
  const [todayDuties, setTodayDuties] = useState<DutyAssignment[]>([]);
  const [tomorrowDuties, setTomorrowDuties] = useState<DutyAssignment[]>([]);
  const [dayAfterDuties, setDayAfterDuties] = useState<DutyAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date();
  dayAfter.setDate(dayAfter.getDate() + 2);

  const todayISO = getDateISO(today);
  const tomorrowISO = getDateISO(tomorrow);
  const dayAfterISO = getDateISO(dayAfter);

  const loadDuties = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    const [td, t, d] = await Promise.all([
      dutyService.search({ dutyDate: todayISO }),
      dutyService.search({ dutyDate: tomorrowISO }),
      dutyService.search({ dutyDate: dayAfterISO }),
    ]);

    setTodayDuties(td.filter((duty) => duty.status !== 'Completed'));
    setTomorrowDuties(t.filter((duty) => duty.status !== 'Completed'));
    setDayAfterDuties(d.filter((duty) => duty.status !== 'Completed'));

    if (!silent) setLoading(false);
    else setRefreshing(false);
  }, [todayISO, tomorrowISO, dayAfterISO]);

  useEffect(() => {
    loadDuties();
  }, [loadDuties]);

  // Real-time subscription — auto-refresh on any duty change
  useRealtimeDutyToast(() => loadDuties(true));

  const handleMarkComplete = useCallback(async (duty: DutyAssignment) => {
    setCompletingId(duty.id);
    try {
      const supabase = createClient();

      const updated = await dutyService.update(duty.id, { status: 'Completed' });
      if (!updated) {
        toast.error('Failed to mark duty as completed');
        return;
      }

      const crewName = duty.driver1Name && duty.driver1Name !== '—'
        ? duty.driver1Name
        : duty.conductorName && duty.conductorName !== '—'
          ? duty.conductorName
          : 'Unknown';
      const crewRole = duty.driver1Name && duty.driver1Name !== '—' ? 'Driver' : 'Conductor';

      const { error: feedbackError } = await supabase.from('post_duty_feedback').insert({
        duty_assignment_id: duty.id,
        duty_code: duty.dutyCode,
        crew_name: crewName,
        crew_role: crewRole,
        route: duty.route,
        duty_date: duty.dutyDate,
        actual_departure_time: duty.departureTime,
        actual_arrival_time: duty.returnTime,
        scheduled_departure_time: duty.departureTime,
        scheduled_arrival_time: duty.returnTime,
        passenger_count: 0,
        fuel_used: 0,
        delay_minutes: 0,
        delay_reason: '',
        issues_reported: '',
        issue_severity: 'None',
        duty_completed: true,
        distance_km: 0,
        route_type: 'City',
        overnight_halts: 0,
        meals_provided: 0,
        advance_given: 0,
        calculated_bhatta: 0,
        final_bhatta: 0,
        duty_hours: 0,
        approval_status: 'pending',
        auto_registered: true,
      });

      if (feedbackError) {
        toast.warning('Duty marked complete, but feedback auto-registration failed.');
      } else {
        toast.success(`Duty ${duty.dutyCode} completed! Registered in Post-Duty Feedback.`, {
          duration: 5000,
          action: {
            label: 'Review Now',
            onClick: () => { window.location.href = '/post-duty-feedback'; },
          },
        });
      }

      // Refresh the list to remove completed duty
      await loadDuties(true);
    } catch (err) {
      console.error('handleMarkComplete error:', err);
      toast.error('An error occurred while completing the duty');
    } finally {
      setCompletingId(null);
    }
  }, [loadDuties]);

  const totalUpcoming = todayDuties.length + tomorrowDuties.length + dayAfterDuties.length;

  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--glass-border)]">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-700 text-foreground">Upcoming Duties</h3>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-700 uppercase tracking-wider bg-positive/15 text-positive border border-positive/20">
              <Radio size={9} className="animate-pulse" />
              Live
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {loading
              ? 'Loading…'
              : `${totalUpcoming} ${totalUpcoming === 1 ? 'duty' : 'duties'} over next 3 days`}
            {refreshing && (
              <span className="ml-2 inline-flex items-center gap-1 text-primary">
                <RefreshCw size={10} className="animate-spin" /> Updating…
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadDuties(true)}
            disabled={refreshing}
            className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors duration-150 text-muted-foreground hover:text-foreground disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <Link
            href="/duty-assignment"
            className="flex items-center gap-1.5 text-sm font-600 text-primary hover:text-primary/80 transition-colors duration-150"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Day sections */}
      <DayDuties
        label={`Today — ${getDateLabel(today)}`}
        dateISO={todayISO}
        duties={todayDuties}
        loading={loading}
        defaultExpanded={true}
        completingId={completingId}
        onMarkComplete={handleMarkComplete}
      />
      <DayDuties
        label={`Tomorrow — ${getDateLabel(tomorrow)}`}
        dateISO={tomorrowISO}
        duties={tomorrowDuties}
        loading={loading}
        defaultExpanded={true}
        completingId={completingId}
        onMarkComplete={handleMarkComplete}
      />
      <DayDuties
        label={`Day After — ${getDateLabel(dayAfter)}`}
        dateISO={dayAfterISO}
        duties={dayAfterDuties}
        loading={loading}
        defaultExpanded={false}
        completingId={completingId}
        onMarkComplete={handleMarkComplete}
      />
    </div>
  );
}
